/* eslint-disable @typescript-eslint/indent */
import Product from '@customTypes/product';
import { areChildrenAllowed, isProductUnAvailable } from '@helpers/product';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { CartItemDetails } from '@customTypes/cart-items';
import HotelDetails from '@customTypes/hotel-details';
import useHotSpot from '@hooks/useHotSpot';
import Currency from '@components/common/Currrency/Currency';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { store } from '@context/store';
import useCounterValidation from '@hooks/useCounterValidation';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import DiscountPriceWithSTP from '@components/STP/ProductDetails/DiscountPriceWithSTP';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import FormErrorText from './FormErrorText';
import CounterVariant from './CounterVariant';

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  product: Product;
  hotelDetails: HotelDetails;
  showFairPricing: boolean;
  adultCounter: number;
  childrenCounter: number;
  setChildrenCounter: React.Dispatch<React.SetStateAction<number>>;
  setAdultCounter: React.Dispatch<React.SetStateAction<number>>;
};

function TimeSlotGuestSelector({
  cartData,
  date,
  product,
  hotelDetails,
  showFairPricing,
  adultCounter,
  childrenCounter,
  setChildrenCounter,
  setAdultCounter,
}: Props) {
  const { dispatch, state } = useContext(store);
  const { pricingMarkdownExperiment } = state;
  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();
  const errorRef = useRef<HTMLDivElement>(null);
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const isPricingAbTest = useMemo(
    () => pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME,
    [pricingMarkdownExperiment],
  );

  const { counterValidation, errorMessage, showErrorMessage } = useCounterValidation(
    adultCounter,
    childrenCounter,
    cartData,
    product,
  );
  const handleAdultCounterChange = (adultCount: number) => {
    const maxChildrenPerAdult = adultCount * product.max_children_per_adult;

    if (maxChildrenPerAdult < childrenCounter) {
      setChildrenCounter(maxChildrenPerAdult);
    }
  };

  useEffect(() => {
    if (showErrorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showErrorMessage]);

  return (
    <>
      {/* Adult */}
      <div className="flex flex-row items-center justify-between mt-6">
        <div className="flex flex-col">
          <div className="flex items-baseline">
            <p
              className={`${
                isPricingAbTest ? 'font-rp-pn-regular text-sm' : 'font-rp-pn-semi-bold text-1'
              } leading-6`}
            >
              {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? 'Adults' : 'Adult'}
            </p>

            {product.adult_age_restriction_text && (
              <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                {product.adult_age_restriction_text}
              </p>
            )}
          </div>

          {product.individual_billing && (
            <>
              <div className="flex font-rp-pn-regular items-center text-custom-black">
                {!date && <p className="leading-22px mr-1 text-sm">Starting at</p>}

                {date &&
                product.is_hot_spot &&
                hotelDetails.hot_spot &&
                isDateWithinVisitWindow(date, hotelDetails) ? (
                  <div className={`${date && product.is_hot_spot ? 'mr-2' : ''}`}>
                    <Currency
                      value={applyHotSpotDiscountToTotal(
                        +product.adult_price,
                        hotelDetails.hot_spot,
                        showFairPricing,
                      )}
                      valueClasses="leading-4 text-rp-hot-spot text-md"
                    />
                  </div>
                ) : null}

                {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                  <DiscountPriceWithSTP
                    cartData={cartData}
                    date={date}
                    hotelDetails={hotelDetails}
                    personType="adult"
                    product={product}
                    showFairPricing={showFairPricing}
                    isTimeSlotted
                    individualBilling={product.individual_billing}
                  />
                ) : (
                  <Currency
                    value={getProductPriceForGuestType(product, 'adult', showFairPricing)}
                    valueClasses={`leading-4 ${
                      date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                        ? 'text-rp-disabled text-base line-through'
                        : 'text-custom-black text-md'
                    }`}
                  />
                )}
              </div>

              {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
              showFairPricing ? (
                <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                  {FAIR_PRICING_VARIANT === 'variant1'
                    ? 'Includes fees'
                    : `+$${(+getProductPriceForGuestType(
                        product,
                        'adult',
                        showFairPricing,
                      )).toFixed(0)} in fees`}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center space-x-4">
          <CounterVariant
            dispatch={dispatch}
            setParentCounter={setAdultCounter}
            counter={adultCounter}
            productData={product}
            date={date}
            isDisabled={isProductUnAvailable(product, cartData, hotelDetails, date)}
            personType="adults"
            onChange={handleAdultCounterChange}
            validate={counterValidation}
            ctasUpdateExperiment={ctasUpdateExperiment}
          />
        </div>
      </div>

      {/* Children */}
      {areChildrenAllowed(product) && (
        <div className="flex flex-col border-rp-gray-divider pt-6">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-baseline">
                <p
                  className={`${
                    isPricingAbTest ? 'font-rp-pn-regular text-sm' : 'font-rp-pn-semi-bold text-1'
                  } leading-6`}
                >
                  {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
                    ? 'Children'
                    : 'Child'}
                </p>

                {product.adult_age_restriction_text && (
                  <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                    {product.child_age_restriction_text}
                  </p>
                )}
              </div>

              {product.individual_billing && (
                <>
                  <div className="flex font-rp-pn-regular items-center text-custom-black">
                    {!date && <p className="leading-22px mr-1 text-sm">Starting at</p>}

                    {date &&
                    product.is_hot_spot &&
                    hotelDetails.hot_spot &&
                    isDateWithinVisitWindow(date, hotelDetails) ? (
                      <div className={`${date && product.is_hot_spot ? 'mr-2' : ''}`}>
                        <Currency
                          value={applyHotSpotDiscountToTotal(
                            +product.children_price,
                            hotelDetails.hot_spot,
                            showFairPricing,
                          )}
                          valueClasses="leading-4 text-rp-hot-spot text-md"
                        />
                      </div>
                    ) : null}

                    {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                      <DiscountPriceWithSTP
                        cartData={cartData}
                        date={date}
                        hotelDetails={hotelDetails}
                        personType="child"
                        product={product}
                        showFairPricing={showFairPricing}
                        isTimeSlotted
                        individualBilling={product.individual_billing}
                      />
                    ) : (
                      <Currency
                        value={getProductPriceForGuestType(product, 'child', showFairPricing)}
                        valueClasses={`leading-4 ${
                          date && product.is_hot_spot && isDateWithinVisitWindow(date, hotelDetails)
                            ? 'text-rp-disabled text-base line-through'
                            : 'text-custom-black text-md'
                        }`}
                      />
                    )}
                  </div>

                  {!(date && isProductUnAvailable(product, cartData, hotelDetails, date)) &&
                  showFairPricing ? (
                    <p className="text-sm text-rp-gray whitespace-nowrap d:-mt-1 d:mb-1">
                      {FAIR_PRICING_VARIANT === 'variant1'
                        ? 'Includes fees'
                        : `+$${(+getProductPriceForGuestType(
                            product,
                            'child',
                            showFairPricing,
                          )).toFixed(0)} in fees`}
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <CounterVariant
                dispatch={dispatch}
                setParentCounter={setChildrenCounter}
                counter={childrenCounter}
                productData={product}
                date={date}
                isDisabled={isProductUnAvailable(product, cartData, hotelDetails, date)}
                personType="children"
                validate={counterValidation}
                ctasUpdateExperiment={ctasUpdateExperiment}
              />
            </div>
          </div>
        </div>
      )}

      {/* Infants */}
      {product.infants_allowed && (
        <div className="border-rp-gray-divider flex flex-row items-center justify-between pt-6 border-t mt-7">
          <div className="flex items-baseline">
            <p className="font-rp-pn-semi-bold leading-6 text-18">Infant</p>

            {product.min_child_age && (
              <p className="capitalize font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                Over {product.min_child_age}
              </p>
            )}
          </div>

          <p className="leading-6 mr-1 text-md">Free</p>
        </div>
      )}

      {/* Error message */}
      {showErrorMessage && (
        <div className="mt-6" ref={errorRef}>
          <FormErrorText showInfoIcon text={errorMessage} />
        </div>
      )}
    </>
  );
}

export default TimeSlotGuestSelector;
